import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import {
  Images,
  Tag,
  EnvelopeSimple,
  ArrowUpRight,
  Sparkle,
  PaintBrush,
  Sun,
  Cloud,
  Snowflake,
  Leaf,
} from "@phosphor-icons/react";
import NanoBananaModal from "../components/NanoBananaModal";

const SEASON_ICON = { spring: Leaf, summer: Sun, autumn: Cloud, winter: Snowflake };

const Stat = ({ label, value, to, testid, icon: Icon }) => (
  <Link
    to={to}
    className="group rounded-[28px] border border-[#2c3854] bg-[#141e32] p-8 flex flex-col justify-between hover:border-[#f5ecd9] hover:shadow-[0_20px_40px_-24px_rgba(11,17,32,0.25)] transition-all"
    data-testid={testid}
  >
    <div className="flex items-start justify-between">
      <Icon size={26} weight="duotone" className="text-[#f5ecd9]" />
      <ArrowUpRight size={16} weight="bold" className="text-[#a8b2cc] group-hover:text-[#f5ecd9]" />
    </div>
    <div className="mt-10">
      <div className="font-display text-6xl tracking-tighter leading-none">{value}</div>
      <div className="mt-3 eyebrow">{label}</div>
    </div>
  </Link>
);

export default function AdminDashboard() {
  const [slides, setSlides] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [regenStatus, setRegenStatus] = useState({ running: false, msg: "" });
  const [seasonal, setSeasonal] = useState(null);
  const [seasonalRunning, setSeasonalRunning] = useState(false);

  const reload = () => {
    Promise.allSettled([
      api.get("/admin/slides").then((r) => setSlides(r.data)),
      api.get("/admin/offers").then((r) => setOffers(r.data)),
      api.get("/contact").then((r) => setContacts(r.data)),
      api.get("/admin/seasonal-hero/status").then((r) => setSeasonal(r.data)),
    ]);
  };

  const regeneratePillars = async () => {
    if (regenStatus.running) return;
    if (!window.confirm("Regenerate all 4 section illustrations? Takes ~40s.")) return;
    setRegenStatus({ running: true, msg: "Generating 4 illustrations…" });
    try {
      const { data } = await api.post("/admin/regenerate-pillars");
      const ok = Object.keys(data.regenerated).length;
      const fail = Object.keys(data.failures).length;
      setRegenStatus({
        running: false,
        msg: `Done — ${ok} regenerated${fail ? `, ${fail} failed` : ""}.`,
      });
      // Cache-bust the live brand context by reloading the page
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      setRegenStatus({ running: false, msg: formatApiError(e) });
    }
  };

  const rotateSeasonal = async () => {
    if (seasonalRunning) return;
    if (!window.confirm(`Generate a fresh ${seasonal?.current_season || "seasonal"} hero now?`)) return;
    setSeasonalRunning(true);
    try {
      await api.post("/admin/seasonal-hero/rotate");
      setRegenStatus({ running: false, msg: "Seasonal hero rotated." });
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      setRegenStatus({ running: false, msg: formatApiError(e) });
    } finally {
      setSeasonalRunning(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const unread = contacts.filter((c) => !c.is_read).length;

  return (
    <div className="p-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-3">Overview</div>
          <h1 className="font-display text-5xl">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAiOpen(true)}
            className="btn-primary"
            data-testid="open-nano-banana"
          >
            <Sparkle size={14} weight="fill" />
            Generate with Nano Banana
          </button>
          <div className="eyebrow hidden md:block">
            {new Date().toLocaleDateString("en-ZA", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        <Stat label="Active slides" value={slides.filter((s) => s.active).length} to="/admin/slides" icon={Images} testid="stat-slides" />
        <Stat label="Active offers" value={offers.filter((o) => o.active).length} to="/admin/offers" icon={Tag} testid="stat-offers" />
        <Stat label={`Unread / ${contacts.length} enquiries`} value={unread} to="/admin/contacts" icon={EnvelopeSimple} testid="stat-contacts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-[28px] border border-[#2c3854] bg-[#141e32] p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="eyebrow">Recent enquiries</div>
            <Link
              to="/admin/contacts"
              className="btn-link"
              data-testid="dashboard-view-enquiries"
            >
              View all
            </Link>
          </div>
          {contacts.length === 0 ? (
            <div className="text-[#a8b2cc] text-sm py-12 text-center">No enquiries yet.</div>
          ) : (
            <ul className="divide-y divide-[#c4d5e6]">
              {contacts.slice(0, 5).map((c) => (
                <li key={c.id} className="py-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-display text-lg tracking-tight">{c.name}</div>
                      <div className="text-sm text-[#a8b2cc]">{c.email}</div>
                    </div>
                    {!c.is_read && (
                      <span className="brand-chip bg-[#dda632] text-[#f5ecd9]">NEW</span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-[#a8b2cc] line-clamp-2">{c.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-[28px] bg-[#141e32] text-white p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 rainbow-bar" />
          <div className="relative">
            <div className="eyebrow mb-4" style={{ color: "#dda632" }}>Quick actions</div>
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => setAiOpen(true)}
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors text-left"
                data-testid="quick-nano-banana"
              >
                <span className="font-display text-lg tracking-tight flex items-center gap-2">
                  <Sparkle size={18} weight="fill" className="text-[#dda632]" />
                  Generate hero art (AI)
                </span>
                <ArrowUpRight size={16} weight="bold" />
              </button>
              <button
                onClick={regeneratePillars}
                disabled={regenStatus.running}
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors text-left disabled:opacity-60"
                data-testid="quick-regen-pillars"
              >
                <span className="font-display text-lg tracking-tight flex items-center gap-2">
                  <PaintBrush size={18} weight="fill" className="text-[#af3fa6]" />
                  {regenStatus.running ? "Regenerating pillars…" : "Regenerate all pillars (AI)"}
                </span>
                <ArrowUpRight size={16} weight="bold" />
              </button>
              <button
                onClick={rotateSeasonal}
                disabled={seasonalRunning}
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors text-left disabled:opacity-60"
                data-testid="quick-rotate-seasonal"
              >
                <span className="font-display text-lg tracking-tight flex items-center gap-2">
                  {(() => {
                    const Icon = SEASON_ICON[seasonal?.current_season] || Sun;
                    return <Icon size={18} weight="fill" className="text-[#dda632]" />;
                  })()}
                  {seasonalRunning
                    ? "Rotating hero…"
                    : `Rotate ${seasonal?.current_season || "seasonal"} hero`}
                </span>
                <ArrowUpRight size={16} weight="bold" />
              </button>
              <Link
                to="/admin/slides"
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors"
                data-testid="quick-add-slide"
              >
                <span className="font-display text-lg tracking-tight">Add a new slide</span>
                <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link
                to="/admin/offers"
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors"
                data-testid="quick-add-offer"
              >
                <span className="font-display text-lg tracking-tight">Publish a new offer</span>
                <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link
                to="/"
                target="_blank"
                className="flex items-center justify-between border border-[#1f2a3f] px-5 py-4 rounded-full hover:bg-[#141e32] transition-colors"
                data-testid="quick-view-site"
              >
                <span className="font-display text-lg tracking-tight">Preview live site</span>
                <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <NanoBananaModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onGenerated={() => reload()}
      />

      {regenStatus.msg && (
        <div
          className="fixed bottom-6 right-6 z-40 px-5 py-4 rounded-2xl bg-[#141e32] border border-[#2c3854] text-[#f5ecd9] shadow-2xl max-w-sm"
          data-testid="regen-toast"
        >
          {regenStatus.msg}
        </div>
      )}
    </div>
  );
}
